import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClientButton } from '@/components/client-ui/button';
import { ClientCalendar } from '@/components/client-ui/ClientCalendar';
import { ClientWizardLayout } from '@/components/client-ui/ClientWizardLayout';
import { BookPractitionerSummaryCard } from '@/features/booking/components/BookPractitionerSummaryCard';
import {
  ClientDurationPicker,
  DEFAULT_DURATION_MINUTES,
  type DurationMinutes,
} from '@/features/booking/components/ClientDurationPicker';
import { ClientSlotGrid } from '@/features/booking/components/ClientSlotGrid';
import { useClientCombinedSearch } from '@/features/booking/hooks/useClientSearch';
import { parseWizardSearch, requirePractitioner } from '@/features/booking/state/wizardSearch';
import { deriveStartTimes } from '@/features/booking/utils/deriveStartTimes';

export const Route = createFileRoute('/client/book/when')({
  component: ClientBookWhenStep,
  validateSearch: (s: Record<string, unknown>) => parseWizardSearch(s),
  beforeLoad: ({ search }) => {
    try {
      requirePractitioner(search);
    } catch {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/client/book/practitioner' });
    }
  },
});

function todayISO(): string {
  const d = new Date();
  const y = String(d.getFullYear());
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Mirror of elixaa-core's CLIENT_BOOKING_WINDOW_DAYS (constants.ts). The BE
 *  rejects /practitioners/availability/search for dates outside
 *  [today, today + WINDOW - 1] with a 400. We use this on the calendar's
 *  `max` prop so users can't pick a date the BE would refuse. */
const CLIENT_BOOKING_WINDOW_DAYS = 7;

function addDaysISO(iso: string, days: number): string {
  const [yStr, mStr, dStr] = iso.split('-');
  const d = new Date(Number(yStr), Number(mStr) - 1, Number(dStr));
  d.setDate(d.getDate() + days);
  const y2 = String(d.getFullYear());
  const m2 = String(d.getMonth() + 1).padStart(2, '0');
  const d2 = String(d.getDate()).padStart(2, '0');
  return `${y2}-${m2}-${d2}`;
}

function format12(hm: string): string {
  const [hStr, mStr] = hm.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0
    ? `${String(displayHour)} ${suffix}`
    : `${String(displayHour)}:${String(m).padStart(2, '0')} ${suffix}`;
}

function addMinutes(hm: string, min: number): string {
  const [hStr, mStr] = hm.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  const total = h * 60 + m + min;
  const nh = Math.floor(total / 60);
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
}

const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function formatDateLong(iso: string): string {
  const [yStr, mStr, dStr] = iso.split('-');
  const date = new Date(Number(yStr), Number(mStr) - 1, Number(dStr));
  const dow = WEEKDAYS_SHORT[date.getDay()] ?? '';
  const month = MONTHS_LONG[Number(mStr) - 1] ?? '';
  // Strip leading zero from day so "05" reads as "5".
  const day = String(Number(dStr));
  return `${dow} · ${day} ${month} ${yStr ?? ''}`;
}

function ClientBookWhenStep() {
  const { t } = useTranslation('clientBooking');
  const navigate = useNavigate();
  const search = Route.useSearch();

  // Clamp the URL-provided date into the BE's allowed window so a stale or
  // hand-edited URL doesn't trigger a guaranteed-error search request (and
  // wipe the practitioner row from view via the empty error response).
  const initialDate = (() => {
    const today = todayISO();
    const max = addDaysISO(today, CLIENT_BOOKING_WINDOW_DAYS - 1);
    const fromUrl = search.date ?? today;
    if (fromUrl < today) return today;
    if (fromUrl > max) return max;
    return fromUrl;
  })();
  const [date, setDate] = useState<string>(initialDate);
  const [duration, setDuration] = useState<DurationMinutes>(
    search.durationMinutes ?? DEFAULT_DURATION_MINUTES,
  );
  const [startTime, setStartTime] = useState<string | null>(search.startTime ?? null);

  const searchQuery = useClientCombinedSearch({ date, mode: undefined });

  const slots = useMemo(() => {
    const own = (searchQuery.data?.results ?? []).find(
      (r) => r.practitioner.id === search.practitionerId,
    );
    if (!own) return [];
    const derived = deriveStartTimes(
      own.slots.map((s) => ({
        start: s.startTime,
        end: s.endTime,
        mode: s.mode === 'offline' ? 'in_person' : 'online',
      })),
      duration,
    );
    // Hide past times when the selected date is today — can't book a slot
    // that's already started.
    if (date === todayISO()) {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      const cutoff = `${hh}:${mm}`;
      return derived.filter((s) => s.time > cutoff);
    }
    return derived;
  }, [searchQuery.data, search.practitionerId, duration, date]);

  const own = (searchQuery.data?.results ?? []).find(
    (r) => r.practitioner.id === search.practitionerId,
  );

  useEffect(() => {
    if (startTime !== null && !slots.some((s) => s.time === startTime)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStartTime(null);
    }
  }, [slots, startTime]);

  const onContinue = () => {
    if (startTime === null) return;
    void navigate({
      to: '/client/book/details',
      search: { ...search, date, startTime, durationMinutes: duration },
    });
  };

  const onBack = () => {
    void navigate({ to: '/client/book/practitioner', search });
  };

  const dateLine = formatDateLong(date);
  const timeLine =
    startTime !== null
      ? `${format12(startTime)} - ${format12(addMinutes(startTime, duration))} (${String(duration)} MINS)`
      : t('wizard.step.when.summaryUnselected');

  // Footer per Figma 2963:3879 — 20px padding, 24px radius. Summary box is a
  // bordered pill with stacked date (muted Regular) + time (foreground Medium).
  // Buttons are 140px each, both ClientButton variants so they pick up the
  // project's button system (focus ring, brand fill).
  //
  // PANEL rather than glass, decided by A/B rather than by tier. The footer is
  // the last thing on a 2630px page, i.e. it sits over the warm end of the page
  // wash (#f4efea -> #fdefe1), and a 62% white glass fill lets that warmth
  // through so the fill contributes almost nothing. Edge-vs-wash max per
  // channel L/T/R, same DOM, same scroll position:
  //     @375   flat 25/20/30   glass 20/18/21   panel 32/26/35
  //     @1280  flat 21/17/27   glass 16/13/21   panel 26/20/32
  // Glass was the only variant that scored WORSE than the flat fill it
  // replaced. Nothing here inverts under an opaque parent: the one child
  // surface is the date/time pill, which is border-only with no fill.
  // Safe for T1 — the footer is in normal flow inside ClientWizardLayout
  // (`<div className="w-full max-w-[600px]">{footer}</div>`), never fixed, so
  // the utility's position:relative is inert.
  const footer: ReactNode = (
    <div className="bg-client-panel rounded-client-xl flex flex-col items-stretch justify-center gap-3 p-4 sm:flex-row sm:items-center sm:gap-5 sm:p-5">
      <div className="border-client-line-200 flex min-w-px flex-1 flex-col items-center justify-center gap-0.5 rounded-[14px] border px-3 py-1.5 text-[13px] sm:text-[14px] sm:whitespace-nowrap">
        {startTime !== null ? (
          <>
            <span className="text-center leading-5 text-[var(--color-client-muted-foreground-soft)] sm:leading-6">
              {dateLine}
            </span>
            <span className="text-center leading-5 font-medium text-[var(--color-client-foreground)] sm:leading-6">
              {timeLine}
            </span>
          </>
        ) : (
          <span className="text-center leading-5 text-[var(--color-client-muted-foreground-soft)] sm:leading-6">
            {timeLine}
          </span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2 sm:gap-2.5">
        <ClientButton
          variant="secondary"
          onClick={onBack}
          className="h-10 flex-1 rounded-[12px] sm:w-35 sm:flex-initial"
        >
          {t('wizard.step.when.back')}
        </ClientButton>
        <ClientButton
          variant="primary"
          disabled={startTime === null}
          onClick={onContinue}
          className="h-10 flex-1 rounded-[12px] sm:w-35 sm:flex-initial"
        >
          {t('wizard.step.when.continue')}
        </ClientButton>
      </div>
    </div>
  );

  return (
    <ClientWizardLayout current={2} contentMaxWidth="wide" footer={footer}>
      <div className="flex flex-col items-center gap-6">
        <div className="w-full max-w-[600px]">
          <BookPractitionerSummaryCard
            practitioner={own !== undefined ? own.practitioner : null}
            eyebrow={t('wizard.step.when.summaryEyebrow')}
            changeLabel={t('wizard.step.when.change')}
            onChange={onBack}
          />
        </div>

        {/* Step 2's decision card — PANEL, opaque, for two independent reasons.
            (1) It hosts an opaque white ClientCalendar and an opaque #f6f6f6
            slot rail; a 62%-translucent parent would sit under both. (2) Cost:
            this card is 2630px tall at 375 and carries up to 91 time chips, so
            backdrop-filter: blur(16px) saturate(1.5) here is a large, repeated
            rasterise on every scroll frame — the dashboard's glass cards are
            small and sit over a static wash, which is why the blur is cheap
            there and would not be here.
            The old `border-client-line-100 border` is REMOVED, not kept: the
            utility already draws a 1px 135deg ring as ::after, and leaving the
            flat border would paint a second, weaker line just inside it. That
            border was measuring only a ~10-unit step anyway. */}
        <div className="bg-client-panel rounded-client-xl w-full max-w-[1100px] p-4 sm:p-5">
          {/* Session Duration row — eyebrow + chips inline per Figma 2946:786 */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-5">
            <Eyebrow>{t('wizard.step.when.sectionDuration')}</Eyebrow>
            <ClientDurationPicker value={duration} onChange={setDuration} />
          </div>
          <hr className="border-client-line-100 my-5" />
          {/* Date & Time eyebrow + inline calendar (left) + slot panel (right) */}
          <div className="flex flex-col gap-4">
            <Eyebrow>{t('wizard.step.when.sectionDateTime')}</Eyebrow>
            <div className="border-client-line-200 flex flex-col overflow-hidden rounded-[14px] border lg:flex-row">
              <div className="bg-client-surface flex shrink-0 justify-center lg:block">
                <ClientCalendar
                  value={date}
                  onChange={setDate}
                  min={todayISO()}
                  max={addDaysISO(todayISO(), CLIENT_BOOKING_WINDOW_DAYS - 1)}
                  className="w-full max-w-full rounded-none border-0 lg:w-[400px] lg:max-w-none"
                />
              </div>
              <div className="min-w-0 flex-1 bg-[var(--color-client-line-50)]">
                <ClientSlotGrid
                  slots={slots}
                  value={startTime}
                  onChange={setStartTime}
                  durationMinutes={duration}
                  isLoading={searchQuery.isLoading}
                  className="h-full rounded-none"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ClientWizardLayout>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="font-client-display block text-[14px] leading-6 font-medium tracking-[var(--tracking-client-mono-eyebrow)] text-[var(--color-client-foreground)] uppercase">
      {children}
    </span>
  );
}
